import { makeAutoObservable, runInAction } from 'mobx';

import { SECURE_STORE_KEYS } from '@/constants/api';
import { clearPendingReferral, getPendingReferral } from '@/lib/pendingReferral';
import { secureStore } from '@/lib/secureStore';
import { accountMergeService } from '@/services/accountMerge';
import { apiClient } from '@/services/api';
import { authService, type AuthChannel } from '@/services/auth';
import { isApiError } from '@/services/errors';
import type { RequestPinResponse, User } from '@/types/user';

// Used only when the user payload predates minimum_cashout_cents (pre-2026-07
// server). Matches the prod value at the time the field shipped.
const FALLBACK_MINIMUM_CASHOUT_CENTS = 500;

class AuthStore {
  sessionToken: string | null = null;
  user: User | null = null;
  needsOnboarding = false;
  hydrated = false;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
    apiClient.setTokenGetter(() => this.sessionToken);
    apiClient.onUnauthenticated(() => {
      void this.clear();
    });
  }

  get isAuthenticated(): boolean {
    return Boolean(this.sessionToken && this.user);
  }

  /**
   * Cash-out minimum in cents, server-owned (env MINIMUM_CASHOUT_CENTS,
   * mirrored onto every user payload). Gates the Cash Out CTA; the server
   * independently enforces it on POST /cash_outs. Floored at 1¢ so a
   * malformed served value (0/negative) can never enable the CTA on a
   * zero balance.
   */
  get minimumCashoutCents(): number {
    return Math.max(1, this.user?.minimum_cashout_cents ?? FALLBACK_MINIMUM_CASHOUT_CENTS);
  }

  async hydrate(): Promise<void> {
    if (this.hydrated) return;
    try {
      const token = await secureStore.get(SECURE_STORE_KEYS.sessionToken);
      if (!token) return;
      runInAction(() => {
        this.sessionToken = token;
      });
      try {
        const user = await authService.me();
        runInAction(() => {
          this.user = user;
          // May 5: email replaced school_id as the onboarding gate.
          // Public-signup captures email up front; PIN-only signups need
          // to fill it in before transacting. School became optional.
          this.needsOnboarding = !user.first_name || !user.email;
        });
      } catch (err) {
        if (isApiError(err) && err.status === 401) {
          // The api client fired `unauthenticated` synchronously inside
          // `toApiError`, but the listener's clear() is async. Await it here
          // so we never expose the brief window where hydrated=true with a
          // stale sessionToken still in the observable.
          await this.clear();
        } else {
          if (__DEV__) console.warn('[auth] me() failed during hydrate', err);
        }
      }
    } finally {
      runInAction(() => {
        this.hydrated = true;
      });
    }
  }

  async requestPin(channel: AuthChannel, contact: string): Promise<RequestPinResponse> {
    return authService.requestPin(channel, contact);
  }

  async requestPinByEmail(contact: string): Promise<void> {
    await authService.requestPinByEmail(contact);
  }

  /**
   * Returns whether this verify created a brand-new account, so the
   * caller can fire registration analytics exactly once per account.
   */
  async verifyPin(contact: string, code: string): Promise<{ newUser: boolean }> {
    const pendingReferral = await getPendingReferral().catch((err) => {
      if (__DEV__) console.warn('[auth] failed to read pending referral', err);
      return null;
    });
    const envelope = pendingReferral
      ? await authService.verifyPin(contact, code, pendingReferral)
      : await authService.verifyPin(contact, code);
    await this.persist(envelope.session_token);
    runInAction(() => {
      this.user = envelope.user;
      this.needsOnboarding = envelope.needs_onboarding;
    });
    if (pendingReferral) {
      await clearPendingReferral().catch((err) => {
        if (__DEV__) console.warn('[auth] failed to clear pending referral', err);
      });
    }
    return { newUser: envelope.new_user === true };
  }

  /**
   * Verified-PIN merge (campaign email collision): the server tombstones
   * the current bare phone account and this session becomes the account
   * that owns the email. The old token died with the merge, so the new one
   * is persisted before anything else can fire a request on the dead one.
   */
  async mergeIntoEmailAccount(email: string, code: string): Promise<void> {
    const envelope = await accountMergeService.confirm(email, code);
    await this.persist(envelope.session_token);
    runInAction(() => {
      this.user = envelope.user;
      this.needsOnboarding = !envelope.user.first_name || !envelope.user.email;
    });
  }

  async persist(token: string): Promise<void> {
    await secureStore.set(SECURE_STORE_KEYS.sessionToken, token);
    runInAction(() => {
      this.sessionToken = token;
    });
  }

  async logout(): Promise<void> {
    try {
      if (this.sessionToken) {
        await authService.logout();
      }
    } catch {
      /* even if the server call fails, we drop local state below */
    }
    await this.clear();
  }

  /** Show an email change the server already saved, ahead of the next refresh. */
  applySavedEmail(email: string): void {
    if (this.user) this.user = { ...this.user, email };
  }

  async refreshMe(): Promise<void> {
    if (!this.sessionToken) return;
    const user = await authService.me();
    runInAction(() => {
      this.user = user;
      this.needsOnboarding = !user.first_name || !user.email;
    });
  }

  async clear(): Promise<void> {
    await secureStore.remove(SECURE_STORE_KEYS.sessionToken);
    runInAction(() => {
      this.sessionToken = null;
      this.user = null;
      this.needsOnboarding = false;
    });
  }
}

export const authStore = new AuthStore();
